import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Wallet } from "lucide-react";

const brl = (v) => (v == null ? "—" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const toInput = (v) => (v == null ? "" : v.toFixed(2).replace(".", ","));

function parseMoney(s) {
  const t = String(s ?? "").replace(/[^\d.,]/g, "");
  if (!t) return null;
  let n;
  if (t.includes(",")) n = t.replace(/\./g, "").replace(",", ".");
  else if (/^\d+\.\d{1,2}$/.test(t)) n = t;
  else n = t.replace(/\./g, "");
  const v = parseFloat(n);
  return Number.isFinite(v) ? v : NaN;
}

const inputCls = "w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-transparent pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500";

function MoneyInput({ label, value, onChange, testId }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">{label}</p>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">R$</span>
        <input type="text" inputMode="decimal" className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} placeholder="0,00" data-testid={testId} />
      </div>
    </div>
  );
}

export default function FinancialCard({ orderId }) {
  const [fin, setFin] = useState(undefined); // undefined = carregando, false = sem acesso
  const [cost, setCost] = useState("");
  const [finalV, setFinalV] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setFin(undefined);
    api.get(`/orders/${orderId}/financial`)
      .then((r) => { if (!alive) return; setFin(r.data); setCost(toInput(r.data.cost)); setFinalV(toInput(r.data.final_value)); })
      .catch(() => { if (alive) setFin(false); });
    return () => { alive = false; };
  }, [orderId]);

  if (!fin) return null;

  const c = parseMoney(cost), f = parseMoney(finalV);
  const invalid = Number.isNaN(c) || Number.isNaN(f);
  const dirty = !invalid && (c !== fin.cost || f !== fin.final_value);
  const margin = !invalid && c != null && f != null ? f - c : null;
  const pct = margin != null && f > 0 ? (margin / f) * 100 : null;

  const save = async () => {
    if (invalid) { toast.error("Valor inválido"); return; }
    setSaving(true);
    try {
      const r = await api.put(`/orders/${orderId}/financial`, { cost: c, final_value: f });
      setFin(r.data); setCost(toInput(r.data.cost)); setFinalV(toInput(r.data.final_value));
      toast.success("Valores salvos");
    } catch (e) { toast.error(errMsg(e, "Erro ao salvar")); } finally { setSaving(false); }
  };

  return (
    <Card className="p-5 border-slate-200 dark:border-slate-800 space-y-3" data-testid="financial-card">
      <h4 className="font-bold text-slate-900 dark:text-white flex items-center gap-2"><Wallet className="w-4 h-4" /> Financeiro</h4>
      <MoneyInput label="Custo da obra" value={cost} onChange={setCost} testId="fin-cost-input" />
      <MoneyInput label="Valor final" value={finalV} onChange={setFinalV} testId="fin-final-input" />
      <div className="flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-sm" data-testid="fin-margin">
        <span className="text-slate-500">Margem</span>
        <span className={`font-semibold ${margin == null ? "text-slate-400" : margin >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
          {brl(margin)}{pct != null ? ` (${pct.toFixed(1).replace(".", ",")}%)` : ""}
        </span>
      </div>
      {dirty && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={saving} data-testid="fin-save-btn">{saving ? "Salvando..." : "Salvar"}</Button>}
      {fin.updated_at && <p className="text-xs text-slate-400">Última alteração por {fin.updated_by_name || "—"} em {fmtDate(fin.updated_at)}</p>}
    </Card>
  );
}
